import React, { useState, useEffect, useRef, ChangeEvent } from "react";
import { apiService } from "../../utils/apiService";
import {
  Activity,
  AlertTriangle,
  Search,
  Shield,
  UserCheck,
  User,
  UserCog,
  Crown,
  HelpCircle,
  Download,
  ChevronUp,
  X,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { format } from "date-fns";

interface LogEntry {
  id: number;
  user_name: string | null;
  user_role: string | null;
  action: string | null;
  description: string | null;
  ip_address: string | null;
  created_at: string | null;
}

const ITEMS_PER_PAGE = 5;

export function ActivityLogs() {
  const [showExportModal, setShowExportModal] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<LogEntry[]>([]);
  const [displayedLogs, setDisplayedLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAction, setSelectedAction] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const isFiltering =
    searchTerm.trim() !== "" || selectedAction !== "all";

  const safeText = (value: unknown, fallback = "N/A"): string => {
    if (typeof value !== "string") return fallback;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : fallback;
  };

  const safeDate = (value: unknown): Date | null => {
    if (!value) return null;
    const parsed = new Date(String(value));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatDateSafe = (
    value: unknown,
    pattern: string,
    fallback = "N/A",
  ): string => {
    const parsed = safeDate(value);
    if (!parsed) return fallback;
    return format(parsed, pattern);
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (tableContainerRef.current) {
        const scrollTop = tableContainerRef.current.scrollTop;
        setShowScrollTop(scrollTop > 300);
      }
    };
    const container = tableContainerRef.current;
    container?.addEventListener("scroll", handleScroll);
    return () => container?.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [logs, searchTerm, selectedAction]);

  useEffect(() => {
    if (isFiltering) {
      setDisplayedLogs(filteredLogs);
      setPage(1);
      setHasMore(false);
    } else {
      setPage(1);
      setDisplayedLogs(filteredLogs.slice(0, ITEMS_PER_PAGE));
      setHasMore(filteredLogs.length > ITEMS_PER_PAGE);
    }
  }, [filteredLogs, isFiltering]);

  useEffect(() => {
    if (isFiltering) return;

    const start = 0;
    const end = page * ITEMS_PER_PAGE;
    setDisplayedLogs(filteredLogs.slice(start, end));
    setHasMore(end < filteredLogs.length);
  }, [page, filteredLogs, isFiltering]);

  useEffect(() => {
    if (
      isFiltering ||
      loading ||
      loadingMore ||
      !hasMore ||
      !loadMoreRef.current ||
      !tableContainerRef.current
    ) {
      if (observerRef.current) observerRef.current.disconnect();
      return;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          setLoadingMore(true);
          setTimeout(() => {
            setPage((prev) => prev + 1);
            setLoadingMore(false);
          }, 300);
        }
      },
      {
        root: tableContainerRef.current,
        rootMargin: "0px 0px 150px 0px",
        threshold: 0.1,
      },
    );

    observerRef.current.observe(loadMoreRef.current);

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [loading, loadingMore, hasMore, filteredLogs.length, page, isFiltering]);

  const fetchLogs = async () => {
    try {
      const response = await apiService.getLogs();
      if (response.data) {
        setLogs(response.data);
        setFilteredLogs(response.data);
      }
    } catch (err) {
      console.error("Failed to fetch logs", err);
      setError("Failed to load activity history.");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    await fetchLogs();
  };

  const scrollToTop = () => {
    tableContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleExport = () => {
    setShowExportModal(true);
  };

  const confirmExport = () => {
    const csv = [
      ["Timestamp", "User", "Role", "Action", "Description", "IP Address"],
      ...filteredLogs.map((log) => [
        formatDateSafe(log.created_at, "MMM d, yyyy h:mm a"),
        safeText(log.user_name),
        safeText(log.user_role),
        getActionLabel(log.action),
        safeText(log.description),
        safeText(log.ip_address),
      ]),
    ]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `activity-logs-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    setShowExportModal(false);
  };
  const getActionLabel = (action: string | null): string => {
    const normalized = safeText(action, "");
    if (!normalized) return "Unknown";
    const upper = normalized.toUpperCase();
    if (upper.includes("DEACTIVATE") || upper.includes("DELETE"))
      return "Delete";
    if (upper.includes("CREATE") || upper.includes("ADD")) return "Add";
    if (upper.includes("UPDATE") || upper.includes("EDIT")) return "Edit";
    if (upper.includes("LOGIN") || upper.includes("SIGN_IN")) return "Login";
    return normalized
      .split(/[_-]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const getActionColor = (action: string | null) => {
    const upper = safeText(action, "").toUpperCase();
    if (upper.includes("DELETE") || upper.includes("DEACTIVATE")) {
      return "border-l-4 border-l-destructive bg-destructive/10 text-destructive";
    }
    return "border-l-4 border-l-primary bg-muted text-foreground";
  };

  const getRoleIcon = (role: string | null) => {
    const r = safeText(role, "").toLowerCase().trim();
    if (r.includes("admin"))
      return (
        <Shield className="h-3.5 w-3.5 text-primary dark:text-muted-foreground" />
      );
    if (r.includes("supervisor") || r.includes("moderator"))
      return (
        <UserCheck className="h-3.5 w-3.5 text-primary dark:text-muted-foreground" />
      );
    if (r.includes("manager") || r.includes("lead"))
      return (
        <UserCog className="h-3.5 w-3.5 text-primary dark:text-muted-foreground" />
      );
    if (r === "user" || r === "member" || r.includes("regular"))
      return <User className="h-3.5 w-3.5 text-primary dark:text-muted-foreground" />;
    if (r.includes("guest") || r.includes("visitor"))
      return (
        <HelpCircle className="h-3.5 w-3.5 text-gray-500 dark:text-gray-400" />
      );
    return <Crown className="h-3.5 w-3.5 text-foreground" />;
  };

  const applyFilters = () => {
    let result = [...logs];

    if (selectedAction !== "all") {
      result = result.filter((log) => {
        const label = getActionLabel(log.action).toLowerCase();
        return label === selectedAction.toLowerCase();
      });
    }

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter((log) => {
        const actionLabel = getActionLabel(log.action).toLowerCase();
        return (
          safeText(log.user_name, "").toLowerCase().includes(term) ||
          safeText(log.user_role, "").toLowerCase().includes(term) ||
          actionLabel.includes(term) ||
          safeText(log.description, "").toLowerCase().includes(term) ||
          safeText(log.ip_address, "").toLowerCase().includes(term) ||
          formatDateSafe(log.created_at, "MMM d, yyyy h:mm a", "")
            .toLowerCase()
            .includes(term)
        );
      });
    }

    setFilteredLogs(result);
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleActionChange = (value: string) => {
    setSelectedAction(value);
  };

  const getUserInitials = (name: string | null): string => {
    const normalizedName = safeText(name, "");
    if (!normalizedName) return "?";
    return normalizedName
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="space-y-6 min-h-screen">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-left">
          <h2 className="text-xl font-bold sm:text-2xl flex items-center">
            <Activity className="mr-2 h-6 w-6 text-foreground" />
            Activity Logs
          </h2>
          <p className="text-sm text-muted-foreground">
            Track and monitor all system activities
          </p>
        </div>

        <div className="flex w-full items-center gap-3 sm:w-auto">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={loading}
            className="w-full sm:w-auto"
          >
            <div className={`h-4 w-4 bg-current mr-2 ${loading ? "animate-spin" : ""}`} />
            <span>Refresh</span>
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-xs">
        <div className="border-b border-border px-6 py-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <h3 className="text-lg font-semibold tracking-tight text-foreground">
                Audit Trail
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {filteredLogs.length > 0
                  ? `Showing ${displayedLogs.length} of ${filteredLogs.length} activities`
                  : "No activities found"}
              </p>
            </div>

            <div className="flex w-full flex-col gap-3 xl:w-auto xl:flex-row">
              <div className="relative w-full xl:min-w-[360px]">
                <Input
                  type="search"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Search by User, Action, Or Description..."
                  className="w-full pl-11 pr-4 text-sm"
                />
                <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              </div>

              <Select
                value={selectedAction}
                onValueChange={handleActionChange}
              >
                <SelectTrigger className="w-full sm:w-auto">
                  <SelectValue placeholder="Filter by Action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Activities</SelectItem>
                  <SelectItem value="add">Add</SelectItem>
                  <SelectItem value="edit">Edit</SelectItem>
                  <SelectItem value="delete">Delete</SelectItem>
                  <SelectItem value="login">Login</SelectItem>
                </SelectContent>
              </Select>

              <Button
                variant="outline"
                onClick={handleExport}
                disabled={displayedLogs.length === 0}
                className="flex w-full items-center justify-center gap-2 sm:w-auto"
              >
                <Download className="h-4 w-4" />
                Export
              </Button>

              {/* Export Modal */}
              {showExportModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4 animate-fade-in-up">
                  <div className="modal w-full max-w-md overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-xl">
                    <div className="border-b border-border p-6">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="flex items-center gap-2.5 text-lg font-semibold text-foreground">
                          <Download className="h-5 w-5 text-foreground" />
                          Export Activity Logs
                        </h3>
                        <button
                          className="rounded-md p-1.5 text-muted-foreground hover:text-foreground"
                          onClick={() => setShowExportModal(false)}
                          aria-label="Close"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                      <p className="mt-1.5 text-sm text-muted-foreground">
                        Are you sure you want to export the currently displayed
                        activity logs?
                      </p>
                    </div>
                    <div className="flex flex-col justify-end gap-3 p-6 sm:flex-row">
                      <Button
                        variant="outline"
                        onClick={() => setShowExportModal(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={confirmExport}
                      >
                        Export
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <p className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-foreground"></span>
            Search across all fields: user name, role, action type, description,
            IP address, and timestamps
          </p>
        </div>

        <div className="bg-card p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-24 text-muted-foreground">
              <div className="relative mb-4 rounded-md bg-muted p-4">
                <div className="h-5 w-5 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
              </div>
              <p className="text-lg font-medium text-foreground">
                Loading activity history...
              </p>
              <p className="text-sm mt-2">Please wait</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-24 text-destructive">
              <div className="mb-4 rounded-md bg-destructive/10 p-4">
                <AlertTriangle className="h-8 w-8" />
              </div>
              <p className="text-lg font-medium text-foreground">{error}</p>
              <Button
                variant="destructive"
                onClick={handleRefresh}
                className="mt-4"
              >
                Try Again
              </Button>
            </div>
          ) : displayedLogs.length === 0 && page === 1 ? (
            <div className="py-24 text-center text-muted-foreground">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-md bg-muted">
                <Activity className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-lg font-medium text-foreground">
                {isFiltering
                  ? "No matching activities found"
                  : "No activity logs found"}
              </p>
              <p className="text-sm mt-2 text-muted-foreground">
                {isFiltering
                  ? "Try adjusting your filters"
                  : "Activities will appear here"}
              </p>
            </div>
          ) : (
            <>
              <div
                ref={tableContainerRef}
                className="relative max-h-[calc(100vh-320px)] overflow-auto border border-border bg-card rounded-md scroll-smooth"
              >
                <table className="min-w-full divide-y divide-border">
                  <thead className="sticky top-0 z-10 bg-muted">
                    <tr>
                      <th
                        scope="col"
                        className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                      >
                        Timestamp
                      </th>
                      <th
                        scope="col"
                        className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                      >
                        User
                      </th>
                      <th
                        scope="col"
                        className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                      >
                        Action
                      </th>
                      <th
                        scope="col"
                        className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                      >
                        Description
                      </th>
                      <th
                        scope="col"
                        className="px-5 py-4 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground"
                      >
                        IP Address
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {displayedLogs.map((log) => (
                      <tr
                        key={log.id}
                        className="hover:bg-accent/50 transition-colors"
                      >
                        <td className="px-5 py-4">
                          <div className="flex flex-col">
                            <div className="text-sm font-medium text-foreground">
                              {formatDateSafe(log.created_at, "MMM d, yyyy")}
                            </div>
                            <div className="mt-1 text-xs text-muted-foreground">
                              {formatDateSafe(log.created_at, "h:mm a")}
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-2.5">
                            <div className="flex-shrink-0">
                              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted border border-border text-xs font-bold text-foreground">
                                {getUserInitials(log.user_name)}
                              </div>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-foreground">
                                {safeText(log.user_name)}
                              </div>
                              <div className="mt-1 flex items-center gap-1.5 text-xs capitalize text-muted-foreground">
                                {getRoleIcon(log.user_role)}
                                <span>{safeText(log.user_role)}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-5 py-4 text-sm">
                          <span
                            className={`inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-md ${getActionColor(log.action)}`}
                          >
                            {getActionLabel(log.action)}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-sm text-muted-foreground">
                          <div
                            className="line-clamp-2 max-w-xs leading-6"
                            title={safeText(log.description)}
                          >
                            {safeText(log.description)}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-5 py-4 text-right font-mono text-sm text-muted-foreground">
                          {safeText(log.ip_address)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Load more trigger — only shown when NOT filtering */}
                {!isFiltering && hasMore && (
                  <div
                    ref={loadMoreRef}
                    className="py-10 flex justify-center items-center min-h-[80px]"
                  >
                    {loadingMore ? (
                      <div className="flex items-center gap-2.5 text-muted-foreground">
                        <div className="h-4 w-4 border-2 border-foreground border-t-transparent rounded-full animate-spin mr-2" />
                        <span>Loading more activities...</span>
                      </div>
                    ) : (
                      <div className="h-10" />
                    )}
                  </div>
                )}

                {!isFiltering && !hasMore && displayedLogs.length > 0 && (
                  <div className="py-8 text-center text-sm font-medium text-muted-foreground">
                    <span className="inline-block rounded-md bg-muted px-4 py-2">
                      You've reached the end
                    </span>
                  </div>
                )}

                {/* Optional message when filtering */}
                {isFiltering && filteredLogs.length > 0 && (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    Showing all {filteredLogs.length} matching activities
                  </div>
                )}

                {showScrollTop && (
                  <button
                    onClick={scrollToTop}
                    className="sticky bottom-5 ml-auto mr-5 flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md hover:opacity-90"
                    aria-label="Scroll to top"
                  >
                    <ChevronUp className="h-5 w-5" />
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
